import React, { useMemo, useState } from 'react';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { fetchAuthenticatedJson } from '../../api/authenticatedFetch';

function StatBadge({ label, value, accent = '#19323c' }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 3,
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(247,243,234,0.12)',
        minWidth: 128,
      }}
    >
      <Typography variant="caption" sx={{ color: 'rgba(247,243,234,0.72)', display: 'block', mb: 0.35 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: accent, fontWeight: 800 }}>
        {value}
      </Typography>
    </Box>
  );
}

function ResultMetric({ label, value, accent = '#19323c' }) {
  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.6,
        py: 1.4,
        borderRadius: 3,
        background: '#fcf8ef',
        border: '1px solid rgba(18,44,52,0.08)',
        minWidth: 132,
      }}
    >
      <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: accent, fontWeight: 800 }}>
        {value}
      </Typography>
    </Paper>
  );
}

const ExportAdminPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const writtenFiles = useMemo(
    () => (Array.isArray(result?.writtenFiles) ? result.writtenFiles : []),
    [result?.writtenFiles],
  );
  const skippedFiles = useMemo(
    () => (Array.isArray(result?.skippedFiles) ? result.skippedFiles : []),
    [result?.skippedFiles],
  );
  const headerOnlyCount = useMemo(
    () => writtenFiles.filter((file) => file.headerOnly).length,
    [writtenFiles],
  );
  const dataFileCount = writtenFiles.length - headerOnlyCount;

  const handleExport = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const payload = await fetchAuthenticatedJson('/api/admin/export/all-csv', { method: 'GET' });
      setResult(payload);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(180deg, #f4efe6 0%, #eef3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3.5, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between">
            <Box sx={{ maxWidth: 760 }}>
              <Chip label="Admin Export" sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.25 }}>
                Exporter les tables vers les CSV d'import
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7 }}>
                Ce panneau reecrit les fichiers CSV consommes au demarrage par Liquibase dans src/main/resources/import/.
                Il sert a figer un snapshot de travail cote serveur sans passer par un zip a telecharger.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ alignSelf: 'flex-start' }}>
              <StatBadge label="Mode" value="Serveur -> CSV" accent="#f7f3ea" />
              <StatBadge label="Cible" value="Liquibase import" accent="#f3c65c" />
              <StatBadge label="Dernier export" value={result ? `${result.fileCount} fichiers` : 'Aucun'} accent={result ? '#8fd6b1' : '#f7f3ea'} />
            </Stack>
          </Stack>
        </Paper>

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="stretch">
          <Paper sx={{ flex: 0.92, p: 3, borderRadius: 5, background: '#fffaf2' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
              Declencher l'export
            </Typography>
            <Typography variant="body2" sx={{ color: '#4f646f', mb: 3 }}>
              Chaque execution reecrit les fichiers cibles. Les tables sans lignes peuvent tout de meme produire un CSV avec header pour conserver une structure d'import stable.
            </Typography>

            <Stack spacing={2.2}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 4, background: '#f6f1e5', border: '1px solid rgba(18,44,52,0.08)' }}>
                <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, mb: 0.8 }}>
                  Destination d'ecriture
                </Typography>
                <Typography variant="body2" sx={{ color: '#4f646f', mb: 1.2 }}>
                  Le backend ecrit directement dans le dossier lu au prochain redemarrage de l'application.
                </Typography>
                <Box sx={{ px: 1.4, py: 1.2, borderRadius: 3, background: '#fff', border: '1px solid rgba(18,44,52,0.08)' }}>
                  <Typography variant="body2" sx={{ color: '#19323c', fontFamily: 'Consolas, Monaco, monospace', wordBreak: 'break-all' }}>
                    src/main/resources/import/
                  </Typography>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ p: 2, borderRadius: 4, background: '#f9f4ea', border: '1px solid rgba(18,44,52,0.08)' }}>
                <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, mb: 0.8 }}>
                  Contenu exporte
                </Typography>
                <Typography variant="body2" sx={{ color: '#4f646f' }}>
                  Les entites JPA sont exportees fichier par fichier. Le resume de droite liste les fichiers reecrits, le nombre de lignes de donnees et les fichiers eventuellement ignores faute de header exploitable.
                </Typography>
              </Paper>

              {error && <Alert severity="error">{error}</Alert>}

              <Button
                variant="contained"
                onClick={handleExport}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />}
                sx={{ minHeight: 52, borderRadius: 99, bgcolor: '#17323c', alignSelf: 'flex-start', ':hover': { bgcolor: '#0f2329' } }}
              >
                {loading ? 'Export en cours...' : 'Exporter vers src/main/resources/import'}
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ flex: 1.08, p: 3, borderRadius: 5, background: '#fff' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
              Dernier resultat
            </Typography>
            <Typography variant="body2" sx={{ color: '#4f646f', mb: 3 }}>
              Le prochain export affichera ici le repertoire exact cible ainsi que le detail des fichiers ecrits par le backend.
            </Typography>

            {!result ? (
              <Paper elevation={0} sx={{ p: 2.4, borderRadius: 4, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#19323c', mb: 0.6 }}>
                  Aucun export execute dans cette session
                </Typography>
                <Typography variant="body2" sx={{ color: '#4f646f' }}>
                  Declenchez l'action a gauche pour afficher ici le recapitulatif complet de l'ecriture des CSV.
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={2}>
                <Alert severity="success">{result.message}</Alert>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4} useFlexGap flexWrap="wrap">
                  <ResultMetric label="Fichiers ecrits" value={`${result.fileCount ?? writtenFiles.length}`} accent="#19323c" />
                  <ResultMetric label="Avec donnees" value={`${dataFileCount}`} accent="#157f3b" />
                  <ResultMetric label="Header only" value={`${headerOnlyCount}`} accent="#8f6b10" />
                  <ResultMetric label="Ignores" value={`${skippedFiles.length}`} accent={skippedFiles.length ? '#b33a2f' : '#19323c'} />
                </Stack>

                <Paper elevation={0} sx={{ p: 2, borderRadius: 4, background: '#f8fbff', border: '1px solid rgba(18,44,52,0.08)' }}>
                  <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, mb: 0.8 }}>
                    Repertoire cible
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#4f646f', fontFamily: 'Consolas, Monaco, monospace', wordBreak: 'break-all' }}>
                    {result.targetDirectory}
                  </Typography>
                </Paper>

                {writtenFiles.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, mb: 1.2 }}>
                      Fichiers reecrits
                    </Typography>
                    <Stack spacing={1.1}>
                      {writtenFiles.map((file) => (
                        <Paper key={file.fileName} elevation={0} sx={{ p: 1.5, borderRadius: 3, border: '1px solid rgba(18,44,52,0.08)', background: '#fffaf4' }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                            <Box>
                              <Typography variant="body1" sx={{ color: '#19323c', fontWeight: 700 }}>
                                {file.fileName}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#4f646f' }}>
                                {file.rowCount} ligne(s) de donnees{file.headerOnly ? ' - header uniquement' : ''}
                              </Typography>
                            </Box>
                            <Chip
                              icon={file.headerOnly ? <WarningAmberRoundedIcon /> : <TaskAltRoundedIcon />}
                              label={file.headerOnly ? 'Header only' : 'Ecrit'}
                              sx={{
                                bgcolor: file.headerOnly ? 'rgba(243,198,92,0.16)' : 'rgba(143,214,177,0.18)',
                                color: '#19323c',
                                fontWeight: 700,
                              }}
                            />
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}

                {skippedFiles.length > 0 && (
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 4, background: '#fff4f2', border: '1px solid rgba(179,58,47,0.12)' }}>
                    <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, mb: 1 }}>
                      Fichiers ignores
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                      {skippedFiles.map((fileName) => (
                        <Chip key={fileName} label={fileName} sx={{ bgcolor: '#fff', color: '#7a2f28', fontWeight: 700 }} />
                      ))}
                    </Stack>
                  </Paper>
                )}
              </Stack>
            )}
          </Paper>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ExportAdminPage;
